import videojs from 'video.js';
import { PresentationStatus } from '../../api/presentation';

export class LivePluginStub extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);

        player.loader().onLoadPlayCover(onLoadPlayCover.bind(player));
        player.loader().onLoadPresentation(onLoadPresentation.bind(player));
    }
}

function isLivePresentation(playStatus) {
    return playStatus === PresentationStatus.ScheduledForLive
        || playStatus === PresentationStatus.OpenForLive
        || playStatus === PresentationStatus.Live
        || playStatus === PresentationStatus.LivePaused
        || playStatus === PresentationStatus.LiveEnded;
}

function shouldLoad(presentationId, playCoverData, presentationData) {
    const { playStatus } = playCoverData;
    return isLivePresentation(playStatus);
}

function isLivePlayable(playStatus) {
    return playStatus === PresentationStatus.Live
        || playStatus === PresentationStatus.LivePaused;
}

async function initializeLivePlugin(presentationId, liveOptions, player) {
    //Import live plugin and register with player
    if (!videojs.getPlugin('live')) {
        const { LivePlugin } = await import(/* webpackChunkName: 'live' */ './live.js');
        player.registerPlugin('live', LivePlugin);
    }
    //Initialize live plugin with presentation data
    player.live().init({
        presentationId,
        playStatus: liveOptions.playStatus,
        unixTime: liveOptions.unixTime,
        enableCountdown: liveOptions.enableLiveCountdown,
        delayLiveIndicationUntilPresentationDate: liveOptions.delayLiveIndicationUntilPresentationDate
    });
}

async function onLoadPlayCover(presentationId, playCoverData, presentationData) {
    const player = this;
    if (shouldLoad(...arguments)) {
        await initializeLivePlugin(presentationId, playCoverData, player)
        return {
            playerOptions: {
                playcover: {
                    showPlayButton: isLivePlayable(playCoverData.playStatus)
                        && !(playCoverData.delayLiveIndicationUntilPresentationDate && Date.now() < playCoverData.unixTime)
                }
            }
        }
    }
}

async function onLoadPresentation(presentationId, playCoverData, presentationData) {
    const player = this;
    const { metadata: liveOptions } = presentationData
    //If playcover is hidden (playstatus == null), check if live plugin needs to be added
    if (!playCoverData.playStatus) {
        //Overwrite playStatus based on data from loaded presentation for shouldLoad check
        playCoverData.playStatus = liveOptions.playStatus;

        if (shouldLoad(presentationId, playCoverData, presentationData)) {
            await initializeLivePlugin(presentationId, liveOptions, player)
        }
    }
    const { metadata, streams, playerOptions } = presentationData;
    //Get slidestream from presentation streams
    const slideStream = streams.find(stream => {
        return stream.streamType === 2;
    });

    if (isLivePresentation(metadata.playStatus)) {
        //On media load, if slidestream exists, start looking for new events from last loaded slide event as opposed to first event in presentation
        player.live().lastLiveEventTime = slideStream?.items[slideStream.items.length - 1]?.time ?? -1;

        const { enableDvr, enableLiveCaptions } = playerOptions?.live ?? {};
        player.live().enableDvr = enableDvr ?? false;
        player.live().liveCaptions = enableLiveCaptions ?? false;

        return {
            metadata: {
                bookmark: Infinity
            },
            playerOptions: {
                settings: {
                    enableSeeking: enableDvr,
                    enableSpeedControl: false
                }
            }
        }
    }
    else {
        return Promise.resolve({
            playerOptions: { live: undefined }
        });
    }
}
